import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { fetchSession } from "../lib/api";

export default function Summary() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchSession(id)
      .then((s) => mounted && setSession(s))
      .catch((e) => mounted && setError(e.message));
    return () => {
      mounted = false;
    };
  }, [id]);

  const downloadMarkdown = () => {
    if (!session) return;
    const blob = new Blob([session.summary || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${session.title || "call-summary"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <div className="p-8 text-red-700" data-testid="summary-error">
        {error}
      </div>
    );
  }
  if (!session) {
    return (
      <div className="p-8 text-slate-500" data-testid="summary-loading">
        Loading summary…
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-50">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4" /> New Call
        </Link>
        <div className="font-display font-semibold text-slate-900">{session.title}</div>
        <button
          onClick={downloadMarkdown}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-medium"
          data-testid="download-summary-btn"
        >
          <Download className="h-4 w-4" /> Markdown
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="summary-page">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
            Post-Call Summary
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {session.title}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            Ended {session.ended_at ? new Date(session.ended_at).toLocaleString() : "—"}
          </div>
        </div>

        <article
          className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm whitespace-pre-wrap leading-relaxed text-slate-800"
          data-testid="summary-content"
        >
          {session.summary || "No summary generated."}
        </article>

        <StatsGrid session={session} />
      </main>
    </div>
  );
}

function StatsGrid({ session }) {
  const stat = (label, n, tone) => (
    <div
      className={`rounded-xl border ${tone} p-4`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 tabular-nums">{n}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
      {stat("Pain Points", session.pain_points?.length || 0, "border-orange-200 bg-orange-50")}
      {stat("Goals", session.goals?.length || 0, "border-emerald-200 bg-emerald-50")}
      {stat("Cost of Inaction", session.cost_of_inaction?.length || 0, "border-amber-200 bg-amber-50")}
      {stat("Marked Moments", session.marked_moments?.length || 0, "border-yellow-300 bg-yellow-50")}
    </div>
  );
}
